import React from 'react';
import { Finding, FindingStatus, FindingSeverity, FindingType } from '../types';
import { TablePagination, useTablePagination } from '../../../../shared/TablePagination';
import { useTableSort, SortableTh } from '../../../../shared/useTableSort';

interface FindingListTableProps {
  findings: Finding[];
  loading: boolean;
  filterStatus: 'ALL' | FindingStatus;
  filterSeverity: 'ALL' | FindingSeverity;
  filterType: 'ALL' | FindingType;
  onFilterStatusChange: (status: 'ALL' | FindingStatus) => void;
  onFilterSeverityChange: (severity: 'ALL' | FindingSeverity) => void;
  onFilterTypeChange: (type: 'ALL' | FindingType) => void;
  onSelectFinding: (finding: Finding) => void;
  onOpenCreate: () => void;
}

export const FindingListTable: React.FC<FindingListTableProps> = ({
  findings,
  loading,
  filterStatus,
  filterSeverity,
  filterType,
  onFilterStatusChange,
  onFilterSeverityChange,
  onFilterTypeChange,
  onSelectFinding,
  onOpenCreate,
}) => {
  const filtered = findings.filter((item) => {
    if (filterStatus !== 'ALL' && item.status !== filterStatus) return false;
    if (filterSeverity !== 'ALL' && item.severity !== filterSeverity) return false;
    if (filterType !== 'ALL' && item.findingType !== filterType) return false;
    return true;
  });

  const {
    sortedItems,
    sortField,
    sortDirection,
    requestSort,
  } = useTableSort(filtered);

  const {
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    totalItems,
    paginatedItems,
  } = useTablePagination(sortedItems, 25);

  const getStatusBadge = (status: FindingStatus) => {
    switch (status) {
      case 'DRAFT':
        return <span className="badge-pill badge-neutral">Draft</span>;
      case 'IDENTIFIED':
        return <span className="badge-pill badge-info">Identified</span>;
      case 'VALIDATED':
      case 'CLASSIFIED':
        return <span className="badge-pill badge-info">Validated / Classified</span>;
      case 'ASSIGNED':
        return <span className="badge-pill badge-info">Assigned</span>;
      case 'CORRECTION_REQUIRED':
        return <span className="badge-pill badge-warning">Immediate Containment</span>;
      case 'ROOT_CAUSE_ANALYSIS':
      case 'CAPA_PLANNING':
        return <span className="badge-pill badge-info">RCA / CAPA Planning</span>;
      case 'ACTION_IN_PROGRESS':
        return <span className="badge-pill badge-warning">Action In Progress</span>;
      case 'IMPLEMENTED':
      case 'PENDING_VERIFICATION':
        return <span className="badge-pill badge-warning">Pending Verification</span>;
      case 'VERIFIED':
        return <span className="badge-pill badge-success">Verified</span>;
      case 'EFFECTIVENESS_MONITORING':
        return <span className="badge-pill badge-info">Effectiveness Monitoring</span>;
      case 'CLOSED':
        return <span className="badge-pill badge-success">Closed</span>;
      case 'REOPENED':
        return <span className="badge-pill badge-danger">Reopened</span>;
      case 'EXTENDED':
        return <span className="badge-pill badge-warning">Extended</span>;
      default:
        return <span className="badge-pill badge-neutral">{status}</span>;
    }
  };

  const getSeverityBadge = (sev: FindingSeverity) => {
    switch (sev) {
      case 'CRITICAL':
        return <span className="badge-pill badge-danger">Critical (Level 1)</span>;
      case 'MAJOR':
        return <span className="badge-pill badge-warning">Major (Level 2)</span>;
      case 'MINOR':
        return <span className="badge-pill badge-info">Minor (Level 3)</span>;
      case 'LOW':
      case 'INFORMATIONAL':
        return <span className="badge-pill badge-neutral">Observation</span>;
      default:
        return <span className="badge-pill badge-neutral">{sev}</span>;
    }
  };

  return (
    <div className="card" style={{ padding: '0', overflow: 'hidden' }}>
      {/* Controls Bar */}
      <div style={{ padding: '16px 20px', borderBottom: '1px solid #e2e8f0', display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: '16px', background: '#f8fafc' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <label style={{ fontSize: '12px', fontWeight: 600, color: '#64748b' }}>Status:</label>
            <select
              value={filterStatus}
              onChange={(e) => onFilterStatusChange(e.target.value as any)}
              style={{ fontSize: '13px', border: '1px solid #cbd5e1', borderRadius: '6px', padding: '6px 10px', background: '#ffffff', color: '#1e293b' }}
            >
              <option value="ALL">All Statuses ({findings.length})</option>
              <option value="IDENTIFIED">Identified</option>
              <option value="ASSIGNED">Assigned</option>
              <option value="CORRECTION_REQUIRED">Correction Required</option>
              <option value="ACTION_IN_PROGRESS">Action In Progress</option>
              <option value="PENDING_VERIFICATION">Pending Verification</option>
              <option value="VERIFIED">Verified</option>
              <option value="CLOSED">Closed</option>
              <option value="REOPENED">Reopened</option>
            </select>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <label style={{ fontSize: '12px', fontWeight: 600, color: '#64748b' }}>Severity:</label>
            <select
              value={filterSeverity}
              onChange={(e) => onFilterSeverityChange(e.target.value as any)}
              style={{ fontSize: '13px', border: '1px solid #cbd5e1', borderRadius: '6px', padding: '6px 10px', background: '#ffffff', color: '#1e293b' }}
            >
              <option value="ALL">All Severities</option>
              <option value="CRITICAL">Critical (Level 1)</option>
              <option value="MAJOR">Major (Level 2)</option>
              <option value="MINOR">Minor (Level 3)</option>
              <option value="INFORMATIONAL">Observation / Info</option>
            </select>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <label style={{ fontSize: '12px', fontWeight: 600, color: '#64748b' }}>Type:</label>
            <select
              value={filterType}
              onChange={(e) => onFilterTypeChange(e.target.value as any)}
              style={{ fontSize: '13px', border: '1px solid #cbd5e1', borderRadius: '6px', padding: '6px 10px', background: '#ffffff', color: '#1e293b' }}
            >
              <option value="ALL">All Types</option>
              <option value="NON_COMPLIANCE">Non-Compliance</option>
              <option value="PROCEDURAL_DEVIATION">Procedural Deviation</option>
              <option value="REGULATORY_GAP">Regulatory Gap</option>
              <option value="CONTROL_DEFICIENCY">Control Deficiency</option>
              <option value="SAFETY_DEFICIENCY">Safety Deficiency</option>
            </select>
          </div>
        </div>

        <button
          onClick={onOpenCreate}
          className="btn-primary"
          style={{ width: 'auto', padding: '8px 16px', fontSize: '13px', display: 'inline-flex', alignItems: 'center', gap: '6px' }}
        >
          <span>+ New Finding</span>
        </button>
      </div>

      {/* Table Content */}
      <div className="table-container" style={{ margin: '0', border: 'none', borderRadius: '0' }}>
        <table className="data-table">
          <thead>
            <tr>
              <SortableTh field="findingNumber" sortField={sortField} sortDirection={sortDirection} onSort={requestSort}>
                Finding No
              </SortableTh>
              <SortableTh field="title" sortField={sortField} sortDirection={sortDirection} onSort={requestSort}>
                Title & Statement
              </SortableTh>
              <SortableTh field="severity" sortField={sortField} sortDirection={sortDirection} onSort={requestSort}>
                Type / Severity
              </SortableTh>
              <SortableTh field="sourceType" sortField={sortField} sortDirection={sortDirection} onSort={requestSort}>
                Source
              </SortableTh>
              <SortableTh field="department" sortField={sortField} sortDirection={sortDirection} onSort={requestSort}>
                Department & Owner
              </SortableTh>
              <SortableTh field="targetDueDate" sortField={sortField} sortDirection={sortDirection} onSort={requestSort}>
                Due Date
              </SortableTh>
              <SortableTh field="status" sortField={sortField} sortDirection={sortDirection} onSort={requestSort}>
                Status
              </SortableTh>
              <th style={{ textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {!loading && filtered.length === 0 ? (
              <tr>
                <td colSpan={8} style={{ textAlign: 'center', padding: '32px', color: '#64748b' }}>
                  No quality/safety findings match the selected criteria.
                </td>
              </tr>
            ) : (
              paginatedItems.map((item) => (
                <tr
                  key={item.id}
                  onClick={() => onSelectFinding(item)}
                  style={{ cursor: 'pointer' }}
                >
                  <td style={{ fontFamily: 'monospace', fontWeight: 600, color: '#0284c7' }}>
                    {item.findingNumber}
                  </td>
                  <td style={{ maxWidth: '320px' }}>
                    <div style={{ fontWeight: 600, color: '#0f172a' }}>{item.title}</div>
                    <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {item.statement}
                    </div>
                    {item.isRepeat && (
                      <span className="badge-pill badge-danger" style={{ marginTop: '4px', display: 'inline-block' }}>
                        🔁 Repeat Finding
                      </span>
                    )}
                  </td>
                  <td>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'flex-start' }}>
                      {getSeverityBadge(item.severity)}
                      <span style={{ fontSize: '11px', color: '#64748b' }}>{item.findingType}</span>
                    </div>
                  </td>
                  <td>
                    <span className="badge-pill badge-neutral">
                      {item.sourceType}
                    </span>
                    {item.sourceReference && (
                      <div style={{ fontSize: '11px', fontFamily: 'monospace', color: '#64748b', marginTop: '4px' }}>
                        {item.sourceReference}
                      </div>
                    )}
                  </td>
                  <td>
                    <div style={{ fontWeight: 500, color: '#1e293b' }}>{item.department}</div>
                    <div style={{ fontSize: '12px', color: '#64748b' }}>{item.ownerName}</div>
                  </td>
                  <td style={{ fontFamily: 'monospace', fontSize: '12px' }}>
                    <span style={{ color: new Date(item.dueDate) < new Date() && item.status !== 'CLOSED' ? '#ef4444' : '#475569', fontWeight: new Date(item.dueDate) < new Date() && item.status !== 'CLOSED' ? 700 : 400 }}>
                      {item.dueDate}
                    </span>
                  </td>
                  <td>
                    {getStatusBadge(item.status)}
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onSelectFinding(item);
                      }}
                      className="btn-secondary"
                      style={{ width: 'auto', padding: '6px 12px', fontSize: '12px' }}
                    >
                      Manage →
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <TablePagination
        currentPage={currentPage}
        totalItems={totalItems}
        pageSize={pageSize}
        onPageChange={setCurrentPage}
        onPageSizeChange={setPageSize}
        itemLabel="findings"
      />
    </div>
  );
};
